import {getMixerTab, getUseLegacyAudioToggle} from './logics/dataOps';

import type {HelAudioTypeIndex} from './logics/const';

declare global {
  // eslint-disable-next-line no-var -- NOTE: TypeScriptのグローバルスコープ拡張では「var」が必須
  var g_audioList: (CHELAudio | null)[] | undefined;
}

export class CHELAudio {
  private type: HelAudioTypeIndex;
  private toplay: boolean;
  private stopping: boolean;
  private audio?: HTMLAudioElement;
  private playing: boolean;
  private playBeginTime: number;
  private readonly context?: AudioContext;
  private source?: AudioBufferSourceNode;
  private gainNode?: GainNode;
  private suspended: boolean;

  constructor(type: HelAudioTypeIndex, volume: number) {
    this.type = type;
    this.toplay = false;
    this.stopping = false;
    this.suspended = false;

    if (getUseLegacyAudioToggle()) {
      this.audio = new Audio();

      this.playing = false;
      this.playBeginTime = 0.0;
    } else {
      this.context = new (window.AudioContext ?? window.webkitAudioContext)();
      this.playing = false;
      this.playBeginTime = 0.0;

      if (this.context === undefined) {
        return;
      }
      this.source = this.context.createBufferSource();
      this.gainNode = this.context.createGain();

      this.gainNode.gain.value = volume;

      const that = this;

      this.source.onended = function () {
        const buf = that.source!.buffer;
        const ended = that.source!.onended;

        that.source!.stop();
        that.source = that.context!.createBufferSource();
        that.source.onended = ended;
        that.source.buffer = buf;
        that.source.connect(that.gainNode!);

        that.playing = false;
        that.stopping = false;
      };
    }
  }

  getType = () => this.type;
  isPlaying = () => this.playing;
  getContext = () => this.context;
  getGainNode = () => this.gainNode;

  load(fileName: string, toplay: boolean, loop: boolean) {
    this.toplay = toplay;

    if (getUseLegacyAudioToggle()) {
      this.audio!.src = fileName;
      this.audio!.load();

      const that = this;

      if (toplay) {
        that.play(loop);
      }

      this.audio!.addEventListener(
        'ended',
        function () {
          that.playing = false;
        },
        false
      );
    } else {
      const request = new XMLHttpRequest();
      request.open('GET', fileName, true);
      request.responseType = 'arraybuffer';

      const that = this;

      request.onload = function () {
        that.context!.decodeAudioData(request.response, function (buf) {
          that.source!.buffer = buf;

          that.source!.connect(that.gainNode!);
          that.gainNode!.connect(that.context!.destination);

          if (toplay) {
            that.play(loop);
          }
        });
      };

      request.send();
    }
  }

  play(loop: boolean) {
    if (!this.toplay && this.isPlay()) return;

    if (this.audio !== undefined) {
      this.audio.loop = loop;
      this.audio.play();

      this.playing = true;
    } else {
      this.source!.loop = loop;

      this.source!.start(0);

      this.playBeginTime = this.context!.currentTime;
      this.playing = true;
    }

    this.toplay = false;
  }

  stop() {
    if (this.stopping) return;
    if (!this.playing) return;

    this.stopping = true;

    if (this.audio !== undefined) {
      this.audio.pause();
    } else {
      this.source!.stop();
    }
  }

  isPlay() {
    if (this.toplay) return 1;

    return this.playing ? 1 : 0;
  }

  getPlayTimeAudio() {
    if (!this.playing) return 0;

    if (this.audio !== undefined) {
      return 0;
    } else {
      const parseTime = this.context!.currentTime - this.playBeginTime;

      return parseInt((parseTime * 1000) as unknown as string, 10);
    }
  }

  setVolume(volume: number) {
    if (this.audio === undefined) {
      this.gainNode!.gain.value = getMixerTab() * volume;
    }
  }

  release() {
    this.stop();
  }

  // サスペンド・レジューム
  suspend() {
    if (this.suspended) return;
    if (this.context === null) return;

    this.context?.suspend();

    this.suspended = true;
  }

  resume() {
    if (!this.suspended) return;
    if (this.context === null) return;

    this.context?.resume();

    this.suspended = false;
  }
}

/**
 * AudioList (GET)
 * @returns (CHELAudio|null)[]
 */
export const getAudioList = (): (CHELAudio | null)[] => {
  return globalThis.g_audioList ?? [];
};

/**
 * AudioList(SET)
 * @remarks
 * 必要あればnullを設定
 * @param audio CHELAudioインスタンス
 * @param index 設定可能なインデックス
 */
export const setAudioList = (audio: CHELAudio | null, index: number) => {
  if (!globalThis.g_audioList) {
    return;
  }
  globalThis.g_audioList[index] = audio;
};
